import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { systemJobsApi } from "../../services/systemJobsApi";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { AccessDenied } from "../../components/AccessDenied";
import { JobDetailDrawer } from "../../components/JobDetailDrawer";
import { RunNowDialog } from "../../components/RunNowDialog";
import { Badge } from "../../components/ui/badge";
import { formatJst } from "../../lib/date";
import { formatApiErrorDetail } from "../../lib/api";
import { jobHealthClass, normalizeJobRow, deriveRunCounts } from "../../lib/jobStatusFormat";
import { usePermissions } from "../../hooks/usePermissions";
import { useLocalization } from "../../hooks/useLocalization";

const SUMMARY_BUCKETS = ["HEALTHY", "RUNNING", "WARNING", "FAILED", "DISABLED"];

function bucketFor(displayStatus) {
  return displayStatus === "MOCK" ? "DISABLED" : displayStatus;
}

export default function SystemJobsPage() {
  const { t } = useLocalization();
  const perm = usePermissions();
  const [accessDenied, setAccessDenied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [rows, setRows] = useState([]);
  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get("status") || null);
  const [selectedKey, setSelectedKey] = useState(null);
  const [history, setHistory] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [runningKey, setRunningKey] = useState(null);

  const load = () => {
    setLoading(true);
    setError(false);
    systemJobsApi.list()
      .then((r) => {
        const scheduled = r.data.scheduled_jobs.map((j) => normalizeJobRow(j, true));
        const notScheduled = r.data.not_scheduled_jobs.map((j) => normalizeJobRow(j, false));
        setRows([...scheduled, ...notScheduled]);
      })
      .catch((err) => { if (err.response?.status === 403) setAccessDenied(true); else setError(true); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const handle = () => load();
    window.addEventListener("yoko:refresh-route", handle);
    return () => window.removeEventListener("yoko:refresh-route", handle);
  }, []);

  useEffect(() => {
    if (!selectedKey) { setHistory([]); return; }
    const row = rows.find((r) => r.key === selectedKey);
    if (!row?.enabled) { setHistory([]); return; }
    systemJobsApi.runHistory(selectedKey).then((r) => setHistory(r.data)).catch(() => setHistory([]));
  }, [selectedKey, rows]);

  const summary = useMemo(() => {
    const counts = Object.fromEntries(SUMMARY_BUCKETS.map((b) => [b, 0]));
    rows.forEach((r) => { counts[bucketFor(r.displayStatus)] = (counts[bucketFor(r.displayStatus)] || 0) + 1; });
    return counts;
  }, [rows]);

  const visibleRows = statusFilter ? rows.filter((r) => bucketFor(r.displayStatus) === statusFilter) : rows;
  const selectedRow = rows.find((r) => r.key === selectedKey) || null;

  const handleRunNow = () => {
    if (!selectedRow) return;
    setRunningKey(selectedRow.key);
    systemJobsApi.runNow(selectedRow.key)
      .then((r) => {
        toast.success(t(r.data.status === "SUCCESS" ? "systemJobs.runSucceeded" : "systemJobs.runFailed", { job: selectedRow.label }));
        setConfirmOpen(false);
        load();
      })
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)))
      .finally(() => setRunningKey(null));
  };

  if (accessDenied) return <AccessDenied resourceLabel={t("nav.systemJobs")} />;

  return (
    <div data-testid="system-jobs-page">
      <PageHelpBanner pageKey="system-jobs" title={t("pageHelp.defaultTitle")}>
        <p>{t("systemJobs.help")}</p>
      </PageHelpBanner>
      <h2 className="text-2xl font-bold tracking-tight text-gray-900 mb-6">{t("systemJobs.title")}</h2>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5" data-testid="job-status-summary">
        {SUMMARY_BUCKETS.map((bucket) => (
          <button
            key={bucket}
            type="button"
            data-testid={`job-summary-${bucket.toLowerCase()}`}
            onClick={() => setStatusFilter((cur) => (cur === bucket ? null : bucket))}
            className={`rounded-xl border p-3 text-left transition-all ${jobHealthClass(bucket)} ${statusFilter === bucket ? "ring-2 ring-offset-1 ring-slate-400" : ""}`}
          >
            <p className="text-2xl font-bold">{summary[bucket]}</p>
            <p className="text-xs font-semibold uppercase">{t(`jobHealth.${bucket}`)}</p>
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden overflow-x-auto">
        {loading ? (
          <p className="text-sm text-gray-500 py-8 text-center">{t("common.loading")}</p>
        ) : error ? (
          <div className="text-center py-8" data-testid="system-jobs-error">
            <p className="text-sm text-gray-500 mb-2">{t("systemJobs.loadError")}</p>
            <button className="text-sm text-blue-600 underline" data-testid="system-jobs-retry" onClick={load}>{t("common.retry")}</button>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="system-jobs-table">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
              <tr>
                <th className="text-left px-4 py-2">{t("systemJobs.job")}</th>
                <th className="text-left px-4 py-2">{t("systemJobs.status")}</th>
                <th className="text-left px-4 py-2">{t("systemJobs.mode")}</th>
                <th className="text-left px-4 py-2">{t("systemJobs.schedule")}</th>
                <th className="text-left px-4 py-2">{t("systemJobs.lastRunLabel")}</th>
                <th className="text-left px-4 py-2">{t("systemJobs.duration")}</th>
                <th className="text-left px-4 py-2">{t("systemJobs.processed")}</th>
                <th className="text-left px-4 py-2">{t("systemJobs.succeeded")}</th>
                <th className="text-left px-4 py-2">{t("systemJobs.nextRun")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {visibleRows.map((row) => {
                const counts = deriveRunCounts(row.lastRun);
                return (
                  <tr
                    key={row.key}
                    data-testid={`job-row-${row.key}`}
                    className="cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => setSelectedKey(row.key)}
                  >
                    <td className="px-4 py-2.5 font-medium text-gray-800">{row.label}</td>
                    <td className="px-4 py-2.5"><Badge className={jobHealthClass(row.displayStatus)}>{t(`jobHealth.${row.displayStatus}`)}</Badge></td>
                    <td className="px-4 py-2.5 text-gray-500">{t(`jobMode.${row.mode}`)}</td>
                    <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">{row.schedule}</td>
                    <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">{row.lastRun ? formatJst(row.lastRun.started_at) : "—"}</td>
                    <td className="px-4 py-2.5 text-gray-500">{row.lastRun?.duration_ms != null ? `${row.lastRun.duration_ms} ms` : "—"}</td>
                    <td className="px-4 py-2.5 text-gray-500">{counts.processed}</td>
                    <td className="px-4 py-2.5 text-gray-500">{counts.succeeded}</td>
                    <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">{row.nextRun ? formatJst(row.nextRun) : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <JobDetailDrawer
        job={selectedRow}
        history={history}
        open={!!selectedKey}
        onOpenChange={(open) => { if (!open) setSelectedKey(null); }}
        canManage={perm.canManageSystemJobs}
        onRunNow={() => setConfirmOpen(true)}
        isRunning={runningKey === selectedKey}
      />
      <RunNowDialog
        job={selectedRow}
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        onConfirm={handleRunNow}
        running={runningKey === selectedKey}
      />
    </div>
  );
}
